import React, { useState } from 'react';
import {
  LayoutDashboard,
  Search,
  FolderGit2,
  FileText,
  ChevronDown,
  Compass,
  BookOpen,
  X
} from 'lucide-react';
import { AuditInstance, LearningProgress } from '../../types';

interface AppSidebarProps {
  activeView: string;
  activeAudit: AuditInstance | null;
  progress?: LearningProgress;
  onNavigate: (view: 'learning_overview' | 'keyword_research' | 'content_planning' | 'dashboard' | 'workspace' | 'report' | 'onpage') => void;
  isOpenMobile?: boolean;
  onCloseMobile?: () => void;
}

export const AppSidebar: React.FC<AppSidebarProps> = ({
  activeView,
  onNavigate,
  isOpenMobile = false,
  onCloseMobile,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const showExpanded = isHovered || isOpenMobile;

  const handleItemClick = (view: any) => {
    onNavigate(view);
    if (onCloseMobile) {
      onCloseMobile();
    }
  };

  return (
    <>
      {/* Mobile Backdrop Overlay */}
      {isOpenMobile && (
        <div
          onClick={onCloseMobile}
          className="mobile-only"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(4px)',
            zIndex: 990,
          }}
        />
      )}

      <aside
        className={`app-sidebar ${isOpenMobile ? 'mobile-open' : ''}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          width: showExpanded ? '260px' : '68px',
          height: '100vh',
          background: '#050912',
          borderRight: '1px solid #1e293b',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          flexShrink: 0,
          userSelect: 'none',
          zIndex: 1000,
          transition: 'width 0.25s cubic-bezier(0.4, 0, 0.2, 1), transform 0.25s ease',
          overflow: 'hidden',
          position: isOpenMobile ? 'fixed' : 'relative',
          top: 0,
          left: 0,
          boxShadow: isOpenMobile ? '4px 0 24px rgba(0, 0, 0, 0.8)' : 'none',
        }}
      >
        <div>
          {/* Top Branding Logo */}
          <div
            style={{
              padding: showExpanded ? '20px 16px' : '20px 14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              transition: 'padding 0.25s ease',
            }}
          >
            <div
              onClick={() => handleItemClick('learning_overview')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                cursor: 'pointer',
                flex: 1,
                overflow: 'hidden',
              }}
              title="Zynex Ninja AI SEO Engine"
            >
              {/* Crimson Red Ninja Logo */}
              <div
                style={{
                  position: 'relative',
                  width: '38px',
                  height: '38px',
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #f43f5e 0%, #ef4444 50%, #991b1b 100%)',
                  padding: '2px',
                  boxShadow: '0 0 20px rgba(244, 63, 94, 0.4)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    borderRadius: '8px',
                    background: '#070a12',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M12 2C15 5 16.5 8 16.5 11.5C16.5 15 15 17 12 20C9 17 7.5 15 7.5 11.5C7.5 8 9 5 12 2Z"
                      fill="url(#sidebar_ninja_red)"
                      stroke="#ef4444"
                      strokeWidth="1.2"
                    />
                    <rect x="9" y="8.5" width="6" height="2.2" rx="1" fill="#070a12" />
                    <circle cx="10.4" cy="9.6" r="0.8" fill="#38bdf8" />
                    <circle cx="13.6" cy="9.6" r="0.8" fill="#38bdf8" />
                    <defs>
                      <linearGradient id="sidebar_ninja_red" x1="12" y1="2" x2="12" y2="20">
                        <stop stopColor="#f43f5e" />
                        <stop offset="1" stopColor="#991b1b" />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
              </div>

              <div
                style={{
                  opacity: showExpanded ? 1 : 0,
                  transition: 'opacity 0.2s ease',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                }}
              >
                <div style={{
                  fontFamily: 'var(--font-heading)',
                  fontWeight: 900,
                  fontSize: '16px',
                  letterSpacing: '-0.02em',
                  background: 'linear-gradient(135deg, #ffffff 20%, #f87171 60%, #ef4444 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}>
                  ZYNEX NINJA
                </div>
                <div style={{ fontSize: '10px', color: '#64748b', fontWeight: 600, letterSpacing: '0.05em' }}>
                  SEO PLATFORM
                </div>
              </div>
            </div>

            {/* Close button on mobile */}
            {isOpenMobile && onCloseMobile && (
              <button
                onClick={onCloseMobile}
                className="mobile-only"
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#94a3b8',
                  cursor: 'pointer',
                  padding: '4px',
                }}
              >
                <X size={20} />
              </button>
            )}
          </div>

          {/* Navigation Items */}
          <div style={{ padding: showExpanded ? '0 12px' : '0 10px', transition: 'padding 0.25s ease' }}>
            
            {/* 1. LEARNING SECTION */}
            {showExpanded && (
              <div style={{ fontSize: '10px', fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '1.2px', padding: '8px 12px 6px 12px', whiteSpace: 'nowrap' }}>
                LEARNING
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', marginBottom: '16px', marginTop: !showExpanded ? '8px' : 0 }}>
              <button
                onClick={() => handleItemClick('learning_overview')}
                title="Learning & Methodology"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: showExpanded ? '9px 12px' : '9px',
                  borderRadius: '8px',
                  border: 'none',
                  fontSize: '13px',
                  fontWeight: activeView === 'learning_overview' ? 700 : 500,
                  cursor: 'pointer',
                  background: activeView === 'learning_overview' ? 'rgba(2, 132, 199, 0.18)' : 'transparent',
                  color: activeView === 'learning_overview' ? '#ffffff' : '#94a3b8',
                  transition: 'all 0.15s ease',
                  textAlign: 'left',
                  justifyContent: showExpanded ? 'flex-start' : 'center',
                }}
              >
                <LayoutDashboard size={18} color={activeView === 'learning_overview' ? '#38bdf8' : '#64748b'} />
                {showExpanded && <span style={{ whiteSpace: 'nowrap' }}>Learning Overview</span>}
              </button>
            </div>

            {/* 2. AUDIT SECTION */}
            {showExpanded && (
              <div style={{ fontSize: '10px', fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '1.2px', padding: '8px 12px 6px 12px', whiteSpace: 'nowrap' }}>
                AUDIT
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', marginBottom: '16px' }}>
              <button
                onClick={() => handleItemClick('dashboard')}
                title="Audit History"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: showExpanded ? '9px 12px' : '9px',
                  borderRadius: '8px',
                  border: 'none',
                  fontSize: '13px',
                  fontWeight: (activeView === 'dashboard' || activeView === 'workspace') ? 700 : 500,
                  cursor: 'pointer',
                  background: (activeView === 'dashboard' || activeView === 'workspace') ? 'rgba(2, 132, 199, 0.18)' : 'transparent',
                  color: (activeView === 'dashboard' || activeView === 'workspace') ? '#ffffff' : '#94a3b8',
                  transition: 'all 0.15s ease',
                  textAlign: 'left',
                  justifyContent: showExpanded ? 'flex-start' : 'center',
                }}
              >
                <FolderGit2 size={18} color={(activeView === 'dashboard' || activeView === 'workspace') ? '#38bdf8' : '#64748b'} />
                {showExpanded && <span style={{ whiteSpace: 'nowrap' }}>Technical Audits</span>}
              </button>
              <button
                onClick={() => handleItemClick('report')}
                title="Final Reports"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: showExpanded ? '9px 12px' : '9px',
                  borderRadius: '8px',
                  border: 'none',
                  fontSize: '13px',
                  fontWeight: activeView === 'report' ? 700 : 500,
                  cursor: 'pointer',
                  background: activeView === 'report' ? 'rgba(2, 132, 199, 0.18)' : 'transparent',
                  color: activeView === 'report' ? '#ffffff' : '#94a3b8',
                  transition: 'all 0.15s ease',
                  textAlign: 'left',
                  justifyContent: showExpanded ? 'flex-start' : 'center',
                }}
              >
                <FileText size={18} color={activeView === 'report' ? '#38bdf8' : '#64748b'} />
                {showExpanded && <span style={{ whiteSpace: 'nowrap' }}>Final Reports</span>}
              </button>
            </div>

            {/* 3. RESEARCH SECTION */}
            {showExpanded && (
              <div style={{ fontSize: '10px', fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '1.2px', padding: '8px 12px 6px 12px', whiteSpace: 'nowrap' }}>
                RESEARCH
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', marginBottom: '16px' }}>
              <button
                onClick={() => handleItemClick('keyword_research')}
                title="Keyword Research"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: showExpanded ? '9px 12px' : '9px',
                  borderRadius: '8px',
                  border: 'none',
                  fontSize: '13px',
                  fontWeight: activeView === 'keyword_research' ? 700 : 500,
                  cursor: 'pointer',
                  background: activeView === 'keyword_research' ? 'rgba(2, 132, 199, 0.18)' : 'transparent',
                  color: activeView === 'keyword_research' ? '#ffffff' : '#94a3b8',
                  transition: 'all 0.15s ease',
                  textAlign: 'left',
                  justifyContent: showExpanded ? 'flex-start' : 'center',
                }}
              >
                <Search size={18} color={activeView === 'keyword_research' ? '#38bdf8' : '#64748b'} />
                {showExpanded && <span style={{ whiteSpace: 'nowrap' }}>Keyword Research</span>}
              </button>
            </div>

            {/* 4. CONTENT PLANNING SECTION */}
            {showExpanded && (
              <div style={{ fontSize: '10px', fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '1.2px', padding: '8px 12px 6px 12px', whiteSpace: 'nowrap' }}>
                CONTENT PLANNING
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', marginBottom: '16px' }}>
              <button
                onClick={() => handleItemClick('content_planning')}
                title="Content Planning"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: showExpanded ? '9px 12px' : '9px',
                  borderRadius: '8px',
                  border: 'none',
                  fontSize: '13px',
                  fontWeight: (activeView === 'content_planning' || activeView === 'content_planning_workspace') ? 700 : 500,
                  cursor: 'pointer',
                  background: (activeView === 'content_planning' || activeView === 'content_planning_workspace') ? 'rgba(2, 132, 199, 0.18)' : 'transparent',
                  color: (activeView === 'content_planning' || activeView === 'content_planning_workspace') ? '#ffffff' : '#94a3b8',
                  transition: 'all 0.15s ease',
                  textAlign: 'left',
                  justifyContent: showExpanded ? 'flex-start' : 'center',
                }}
              >
                <BookOpen size={18} color={(activeView === 'content_planning' || activeView === 'content_planning_workspace') ? '#38bdf8' : '#64748b'} />
                {showExpanded && <span style={{ whiteSpace: 'nowrap' }}>Content Planning</span>}
              </button>
            </div>

            {/* 5. ON PAGE SECTION */}
            {showExpanded && (
              <div style={{ fontSize: '10px', fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '1.2px', padding: '8px 12px 6px 12px', whiteSpace: 'nowrap' }}>
                ON PAGE
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', marginBottom: '16px' }}>
              <button
                onClick={() => handleItemClick('onpage')}
                title="On-Page SEO"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: showExpanded ? '9px 12px' : '9px',
                  borderRadius: '8px',
                  border: 'none',
                  fontSize: '13px',
                  fontWeight: (activeView === 'onpage' || activeView === 'onpage_workspace') ? 700 : 500,
                  cursor: 'pointer',
                  background: (activeView === 'onpage' || activeView === 'onpage_workspace') ? 'rgba(2, 132, 199, 0.18)' : 'transparent',
                  color: (activeView === 'onpage' || activeView === 'onpage_workspace') ? '#ffffff' : '#94a3b8',
                  transition: 'all 0.15s ease',
                  textAlign: 'left',
                  justifyContent: showExpanded ? 'flex-start' : 'center',
                }}
              >
                <Compass size={18} color={(activeView === 'onpage' || activeView === 'onpage_workspace') ? '#38bdf8' : '#64748b'} />
                {showExpanded && <span style={{ whiteSpace: 'nowrap' }}>On-Page Engine</span>}
              </button>
            </div>

          </div>
        </div>

        {/* Bottom Profile Section */}
        <div style={{ padding: showExpanded ? '16px' : '16px 10px', borderTop: '1px solid #1e293b', transition: 'padding 0.25s ease' }}>
          {/* User Profile Pill Card (Hamza Chohan) */}
          <div
            style={{
              background: '#090e1a',
              border: '1px solid #1e293b',
              borderRadius: '12px',
              padding: showExpanded ? '10px 12px' : '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: showExpanded ? 'space-between' : 'center',
              cursor: 'pointer',
            }}
            title="Hamza Chohan · SEO Specialist"
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', overflow: 'hidden' }}>
              <div
                style={{
                  width: '34px',
                  height: '34px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: 800,
                  color: '#ffffff',
                  flexShrink: 0,
                }}
              >
                HN
              </div>
              {showExpanded && (
                <div style={{ overflow: 'hidden', whiteSpace: 'nowrap' }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: '#ffffff', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    Hamza Chohan
                  </div>
                  <div style={{ fontSize: '11px', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    SEO Specialist
                  </div>
                </div>
              )}
            </div>
            {showExpanded && <ChevronDown size={14} color="#64748b" />}
          </div>
        </div>
      </aside>
    </>
  );
};
